import React from 'react';
import { X } from 'lucide-react';
import { ChatError } from '../../hooks/useErrorHandler';

interface ChatErrorDisplayProps {
  error: ChatError;
  onDismiss: (errorId: string) => void;
}

export const ChatErrorDisplay: React.FC<ChatErrorDisplayProps> = ({ error, onDismiss }) => {
  const getErrorIcon = (type: ChatError['type']) => {
    switch (type) {
      case 'connection':
        return '🔌';
      case 'auth':
        return '🔐';
      case 'message':
        return '💬';
      case 'session':
        return '📋';
      default:
        return '⚠️';
    }
  };

  const getErrorColor = (type: ChatError['type']) => {
    switch (type) {
      case 'connection':
        return 'border-orange-200 bg-orange-50 text-orange-800';
      case 'auth':
        return 'border-red-200 bg-red-50 text-red-800';
      case 'message':
        return 'border-blue-200 bg-blue-50 text-blue-800';
      case 'session':
        return 'border-purple-200 bg-purple-50 text-purple-800';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800';
    }
  };

  return (
    <div className={`p-3 rounded-lg border flex items-start gap-3 ${getErrorColor(error.type)}`}>
      <div className="flex-shrink-0 mt-0.5">
        <span className="text-sm">{getErrorIcon(error.type)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {error.type.charAt(0).toUpperCase() + error.type.slice(1)} Error
        </p>
        <p className="text-sm mt-1">{error.message}</p>
        {process.env.NODE_ENV === 'development' && error.details && (
          <details className="mt-2">
            <summary className="text-xs cursor-pointer opacity-75">
              Debug Details
            </summary>
            <pre className="text-xs mt-1 whitespace-pre-wrap opacity-75">
              {JSON.stringify(error.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
      <button
        onClick={() => onDismiss(error.id)}
        className="flex-shrink-0 p-1 hover:bg-black/5 rounded"
        aria-label="Dismiss error"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

interface ChatErrorListProps {
  errors: ChatError[];
  onDismiss: (errorId: string) => void;
  className?: string;
}

export const ChatErrorList: React.FC<ChatErrorListProps> = ({ 
  errors, 
  onDismiss, 
  className = '' 
}) => {
  if (errors.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      {errors.map(error => (
        <ChatErrorDisplay
          key={error.id}
          error={error}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};
